// Rene domenefunksjoner for «Hvor er sniken?»-funksjonen.
// Alle funksjoner her er rene (ingen side-effekter, ingen mutasjon av input)
// og opererer kun på Snik/SnikFangstLinje-formen definert i snikTypes.js.
// (Se .kiro/specs/hvor-er-sniken/design.md)

/**
 * @typedef {import('./snikTypes').Snik} Snik
 */

/**
 * @typedef {'aktiv' | 'trukket' | 'alle'} SnikFilter
 */

/**
 * Filtrerer en liste av Sniker basert på status.
 *
 * - 'aktiv' returnerer kun Sniker med status 'aktiv'
 * - 'trukket' returnerer kun Sniker med status 'trukket'
 * - 'alle' returnerer alle Sniker uendret
 *
 * Muterer aldri input-listen.
 *
 * @param {Snik[]} sniker
 * @param {SnikFilter} filter
 * @returns {Snik[]}
 */
export function filtrerSniker(sniker, filter) {
  if (filter === 'alle') {
    return [...sniker];
  }
  if (filter === 'aktiv') {
    return sniker.filter((snik) => snik.status === 'aktiv');
  }
  if (filter === 'trukket') {
    return sniker.filter((snik) => snik.status === 'trukket');
  }
  return [...sniker];
}

/**
 * Formaterer en forløpt varighet (i millisekunder) til en lesbar streng
 * i timer og minutter, f.eks. "2t 14m".
 *
 * - Negative eller manglende verdier (f.eks. ved klokkeavvik) klemmes til 0.
 * - Minutter avrundes nedover (hele minutter, ingen sekunder vises).
 * - Timer vises alltid, også når verdien er 0 (f.eks. "0t 5m", "0t 0m").
 *
 * Ren funksjon: ingen side-effekter, avhenger kun av input.
 *
 * @param {number} elapsedMs Forløpt tid i millisekunder.
 * @returns {string} Formatert varighet, f.eks. "2t 14m".
 */
export function formaterVarighet(elapsedMs) {
  const trygtAntallMs = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  const totalMinutter = Math.floor(trygtAntallMs / 60000);
  const timer = Math.floor(totalMinutter / 60);
  const minutter = totalMinutter % 60;
  return `${timer}t ${minutter}m`;
}

/**
 * Beregner neste ledige Snik_Løpenummer basert på eksisterende Sniker.
 *
 * Returnerer det høyeste eksisterende `lopenummer` + 1, eller 1 hvis
 * listen er tom. Resultatet er uavhengig av rekkefølgen elementene
 * forekommer i listen (Property 4).
 *
 * Ren funksjon: ingen side-effekter, muterer aldri input-listen.
 *
 * @param {Snik[]} eksisterendeSniker
 * @returns {number} Neste ledige løpenummer (>= 1).
 */
export function nesteLopenummer(eksisterendeSniker) {
  if (!Array.isArray(eksisterendeSniker) || eksisterendeSniker.length === 0) {
    return 1;
  }
  const hoyesteLopenummer = eksisterendeSniker.reduce(
    (maks, snik) => Math.max(maks, snik.lopenummer),
    0
  );
  return hoyesteLopenummer + 1;
}

/**
 * Oppretter en ny Snik med korrekt initialtilstand.
 *
 * Den returnerte Snik har alltid status 'aktiv' og `tidspunktTrukket: null`,
 * uavhengig av input. `posisjon`, `tidspunktSatt` og `lopenummer` settes
 * eksakt fra argumentene (posisjon lagres med full presisjon, uendret).
 * `totalTidUteMs` er ikke beregnet ennå (null), `fangst` er en tom liste,
 * og `notat` er en tom streng.
 *
 * Ren funksjon: ingen side-effekter, muterer aldri `posisjon`.
 *
 * @param {import('./snikTypes').SnikPosisjon} posisjon Bekreftet posisjon (full GPS-presisjon).
 * @param {number} tidspunktSatt Tidspunkt Snik ble satt (ms epoch, Date.now()).
 * @param {number} lopenummer Snik_Løpenummer tildelt denne Snik.
 * @returns {import('./snikTypes').Snik} Ny Snik med status 'aktiv'.
 */
export function lagNySnik(posisjon, tidspunktSatt, lopenummer) {
  return {
    posisjon: { ...posisjon },
    tidspunktSatt,
    lopenummer,
    status: 'aktiv',
    tidspunktTrukket: null,
    totalTidUteMs: null,
    notat: '',
    fangst: [],
  };
}

/**
 * Utfører statusovergangen fra 'aktiv' til 'trukket' for en Snik.
 *
 * - Hvis `snik.status` allerede er 'trukket', er dette et no-op: den
 *   samme Snik returneres uendret (ingen ny objektreferanse er påkrevd,
 *   men `tidspunktTrukket`/`totalTidUteMs` overskrives aldri). Dette
 *   sikrer at statusovergangen er envegs (Property 9, Requirement 5.4).
 * - Hvis `snik.status` er 'aktiv', returneres et NYTT Snik-objekt med
 *   status 'trukket', `tidspunktTrukket` satt til det angitte tidspunktet,
 *   og `totalTidUteMs` beregnet som `tidspunktTrukket - snik.tidspunktSatt`
 *   (Requirement 5.1, 5.3, 6.3, 6.4).
 *
 * Ren funksjon: ingen side-effekter, muterer aldri input-Snik.
 *
 * @param {Snik} snik Snik som skal trekkes opp.
 * @param {number} tidspunktTrukket Tidspunkt Snik ble trukket (ms epoch).
 * @returns {Snik} Snik med status 'trukket' (uendret hvis allerede trukket).
 */
export function trekkOpp(snik, tidspunktTrukket) {
  if (snik.status === 'trukket') {
    return snik;
  }
  return {
    ...snik,
    status: 'trukket',
    tidspunktTrukket,
    totalTidUteMs: tidspunktTrukket - snik.tidspunktSatt,
  };
}

/**
 * Beregner forløpt tid (Snik_Stoppeklokke) for en Snik, i millisekunder.
 *
 * - Hvis `snik.status` er 'trukket' (og `tidspunktTrukket` er satt),
 *   returneres `tidspunktTrukket - tidspunktSatt`. Verdien er da låst
 *   og videre tidsforløp (endringer i `naa`) påvirker aldri resultatet
 *   (Requirement 6.3, 6.4).
 * - Hvis `snik.status` er 'aktiv', returneres `naa - tidspunktSatt`,
 *   beregnet på nytt hver gang funksjonen kalles (Requirement 6.2, 6.6).
 *
 * Funksjonen er en ren funksjon av tidsstemplene på `snik` og av `naa`
 * — den leser og lagrer aldri noen mellomlagret/akkumulert forløpt tid
 * (f.eks. `totalTidUteMs` brukes ikke her, selv om den finnes på en
 * trukket Snik), slik at en reload alltid gir korrekt verdi uten
 * manuell inngripen (Property 10).
 *
 * Ren funksjon: ingen side-effekter, muterer aldri input-Snik.
 *
 * @param {Snik} snik Snik det skal beregnes forløpt tid for.
 * @param {number} naa Nåværende tidspunkt (ms epoch), brukt kun når `snik` er aktiv.
 * @returns {number} Forløpt tid i millisekunder (>= 0).
 */
export function beregnStoppeklokke(snik, naa) {
  const sluttTidspunkt =
    snik.status === 'trukket' && snik.tidspunktTrukket != null
      ? snik.tidspunktTrukket
      : naa;
  const elapsedMs = sluttTidspunkt - snik.tidspunktSatt;
  return Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
}

/**
 * @typedef {import('./snikTypes').SnikFangstLinje} SnikFangstLinje
 */

/**
 * Legger til en fiskeart i en Snik_Fangst-liste (valgt via Art_Velger).
 *
 * - Hvis det allerede finnes en linje for nøyaktig denne arten (sammenlignet
 *   på artsnavn, `navn`), økes `antall` på den eksisterende linjen med 1,
 *   i stedet for at det opprettes en ny linje (Requirement 8.7, Property 14).
 * - Hvis arten ikke finnes fra før, legges en ny linje til med `antall: 1`
 *   (Requirement 8.4).
 * - Flere forskjellige arter kan eksistere side ved side i samme liste
 *   (Requirement 8.6).
 *
 * Ren funksjon: muterer aldri `fangst`, returnerer alltid en ny liste.
 *
 * @param {SnikFangstLinje[]} fangst Gjeldende Snik_Fangst-liste.
 * @param {{ navn: string, latin: string }} art Art valgt i Art_Velger.
 * @returns {SnikFangstLinje[]} Ny liste med arten lagt til eller antall økt.
 */
export function leggTilArt(fangst, art) {
  const eksisterendeIndex = fangst.findIndex((linje) => linje.art === art.navn);
  if (eksisterendeIndex === -1) {
    return [...fangst, { art: art.navn, latin: art.latin, antall: 1 }];
  }
  return fangst.map((linje, index) =>
    index === eksisterendeIndex ? { ...linje, antall: linje.antall + 1 } : linje
  );
}

/**
 * Endrer antallet på en artslinje i Snik_Fangst med et gitt delta.
 *
 * - `delta` kan være positiv (pluss-knapp, Requirement 9.2) eller negativ
 *   (minus-knapp, Requirement 9.3).
 * - Denne funksjonen tillater aldri at `antall` går under 1 — resultatet
 *   klemmes til minimum 1. Fjerning av en linje (antall skal til 0) skjer
 *   utelukkende via `fjernArt`, aldri via `endreAntall`.
 * - Hvis ingen linje matcher `art`, returneres listen uendret (som en ny
 *   liste-referanse).
 *
 * Ren funksjon: muterer aldri `fangst`, returnerer alltid en ny liste.
 *
 * @param {SnikFangstLinje[]} fangst Gjeldende Snik_Fangst-liste.
 * @param {string} art Artsnavnet (`navn`) til linjen som skal endres.
 * @param {number} delta Endring i antall, f.eks. +1 eller -1.
 * @returns {SnikFangstLinje[]} Ny liste med oppdatert antall.
 */
export function endreAntall(fangst, art, delta) {
  return fangst.map((linje) =>
    linje.art === art ? { ...linje, antall: Math.max(1, linje.antall + delta) } : linje
  );
}

/**
 * Fjerner artslinjen for en gitt art fra Snik_Fangst, uansett gjeldende antall.
 *
 * Brukes av slett-knappen per artslinje (Requirement 9.5), og av
 * bekreftelsesdialogen som vises når minus trykkes på en linje med antall 1
 * (Requirement 9.4) — selve fjerningen skjer alltid via denne funksjonen.
 *
 * Ren funksjon: muterer aldri `fangst`, returnerer alltid en ny liste.
 *
 * @param {SnikFangstLinje[]} fangst Gjeldende Snik_Fangst-liste.
 * @param {string} art Artsnavnet (`navn`) til linjen som skal fjernes.
 * @returns {SnikFangstLinje[]} Ny liste uten linjen for den angitte arten.
 */
export function fjernArt(fangst, art) {
  return fangst.filter((linje) => linje.art !== art);
}

/**
 * Avgjør hvilken markørtype en Snik skal vises som på Snikkart, basert på
 * `snik.status` og (for trukne Sniker) om det er registrert noen fangst.
 *
 * - `'aktiv'` gir `'aktiv-pulserende'` (grønn, firkantet, pulserende markør).
 * - `'trukket'` med minst én artslinje i `fangst` (uansett art eller antall)
 *   gir `'trukket-fangst'` (gul stjerne) — det ble fanget noe på denne Snik.
 * - `'trukket'` med tom (eller manglende) `fangst` gir `'trukket-tom'`
 *   (rødt kryss) — Snik ble trukket uten registrert fangst.
 *
 * Avhenger kun av `status` og `fangst.length` — ingen andre felt på `snik`
 * påvirker resultatet (Property 7, Requirement 4.1, 4.2).
 *
 * Ren funksjon: ingen side-effekter, muterer aldri input-Snik.
 *
 * @param {Snik} snik Snik det skal bestemmes markørtype for.
 * @returns {'aktiv-pulserende' | 'trukket-fangst' | 'trukket-tom'} Markørtype.
 */
export function markorType(snik) {
  if (snik.status === 'aktiv') {
    return 'aktiv-pulserende';
  }
  const harFangst = Array.isArray(snik.fangst) && snik.fangst.length > 0;
  return harFangst ? 'trukket-fangst' : 'trukket-tom';
}
